import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Children, forwardRef, memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import ResizeObserver from "resize-observer-polyfill";
import { MenuButton } from "../menu-button/MenuButton";
import { useCombineRefs } from "../hooks/useCombineRefs";
import { PopperBox } from "../popper/PopperBox";
import { getClientWidth } from "./get-client-width";
import { DefaultOverflowWrapper, HiddenWrap, OverflowWrapper, Wrapper } from "./OverflowCollapse.styled";
const DefaultRenderOverflow = (children) => {
    const firstChild = Children.toArray(children)[0];
    // @ts-expect-error
    const priority = firstChild ? firstChild.props?.priority : "primary";
    return (_jsx(MenuButton, { placement: "bottom-end", priority: priority, overlay: _jsx(PopperBox, { colorScheme: "alchemyLight", maxWidth: 320, hasPadding: false, children: _jsx(DefaultOverflowWrapper, { children: children }) }), children: "More" }));
};
// If a scrollbar appears after content loads, items may no longer fit,
// so we give 20px of wiggle room just in case (approx. max scrollbar width)
export const potentialScrollBarWidth = 20;
export const OverflowCollapse = memo(forwardRef((props, ref) => {
    const { children, shouldCollapseAll, collapseThreshold = 10, layoutTrigger, renderOverflow = DefaultRenderOverflow, showAll = false, spacing = 0, overflowElement = "div", wrapperElement = "div", ...otherProps } = props;
    const [wrapperWidth, setWrapperWidth] = useState(0);
    const [overflowWidth, setOverflowWidth] = useState(0);
    const [childWidths, setChildWidths] = useState([]);
    const overflowRef = useRef(null);
    const wrapperRef = useRef(null);
    const setWidths = () => {
        setWrapperWidth(getClientWidth(wrapperRef.current) || 0);
        setOverflowWidth(getClientWidth(overflowRef.current) || 0);
        const childrenWithoutOverflow = Array.from(
        // @ts-expect-error
        wrapperRef.current.children).filter((el) => el !== overflowRef.current);
        setChildWidths(
        // @ts-expect-error
        childrenWithoutOverflow.map((child) => getClientWidth(child)));
    };
    const getLastIndex = () => {
        if (childWidths.length === 0) {
            return 0;
        }
        const packItems = (widths, trailingMargin) => {
            if (shouldCollapseAll) {
                return 0;
            }
            let currentWidth = 0;
            return widths.findIndex((w) => {
                const itemWidth = w + spacing;
                if (wrapperWidth - potentialScrollBarWidth - (currentWidth + itemWidth) > trailingMargin) {
                    currentWidth += itemWidth;
                    return false;
                }
                return true;
            });
        };
        const lastIndex = packItems(childWidths, collapseThreshold);
        if (lastIndex > -1 && lastIndex < childWidths.length) {
            // Don't have enough space for all items, need to leave space for overflow component
            return packItems(childWidths, overflowWidth + collapseThreshold);
        }
        return lastIndex;
    };
    useLayoutEffect(setWidths, [layoutTrigger, children]);
    // Bind resize listeners
    useEffect(() => {
        if (!wrapperRef.current) {
            return;
        }
        const resizeHandler = debounce(setWidths, 1000 / 15);
        const resizeObserver = new ResizeObserver(resizeHandler);
        resizeObserver.observe(wrapperRef.current);
        return () => {
            resizeHandler.cancel();
            resizeObserver.disconnect();
        };
    }, []);
    const lastIndex = getLastIndex();
    const childrenArr = Children.toArray(children);
    const visibleItems = showAll
        ? childrenArr
        : childrenArr.slice(0, lastIndex === -1 ? childrenArr.length : lastIndex);
    const showOverflow = !showAll && lastIndex > -1 && lastIndex < childrenArr.length;
    const hiddenItems = showOverflow ? childrenArr.slice(lastIndex, childrenArr.length) : [];
    return (_jsxs(Wrapper, { ref: useCombineRefs(ref, wrapperRef), "data-testid": "OverflowCollapse", ...otherProps, as: wrapperElement && wrapperElement, children: [visibleItems, _jsx(OverflowWrapper, { ref: overflowRef, showOverflow: showOverflow, "aria-hidden": !showOverflow, as: overflowElement && overflowElement, children: renderOverflow(hiddenItems) }), hiddenItems.map((item, i) => (_jsx(HiddenWrap, { children: item }, item.key ?? i)))] }));
}));
OverflowCollapse.displayName = "OverflowCollapse";
